"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as S from "./style";

import { StyleDiv } from "@commons/style";
import Typography from "@components/typography";
import { MultiSelect } from "@components/multi-select";
import { Truck } from "lucide-react";
import Button from "@components/button";
import { useModal } from "@context/ModalContext";
import Row from "@components/row";
import Column from "@components/column";
import { Order } from "@apis/index";
import { toast } from "react-toastify";
import AcceptOrderModal from "@components/orders/actions/AcceptOrderModal";
import RejectOrderModal from "@components/orders/actions/RejectOrderModal";
import OrderProcessingModal from "@components/orders/actions/OrderProcessingModal";
import AssignDeliveryManModal from "@components/orders/actions/AssignDeliveryManModal";
import useConfig from "@hooks/useConfig";
import { Helper } from "@utils/index";

type OptionType = {
  value: string;
  label: string;
};

const OrderShippingCard = ({ order, onStatusUpdate }: any) => {
  const { showModal, hideModal } = useModal();
  const [selectedOrderOption, setSelectedOrderOption] = useState<string>("");
  const [selectedMethodOption, setSelectedMethodOption] = useState<string>("");
  const user = Helper.getUser();
  const isStore = user?.role === "store" || user?.role === "store-employee";

  useEffect(() => {
    if (order?.status) {
      setSelectedOrderOption(order.status);
    }
  }, [order]);

  const orderStatusOptions: OptionType[] = useMemo(() => {
    const isPharma = order?.module?.type === "pharma";
    const status = order?.status;

    // Base options that always show (unless completed/cancelled)
    const baseOptions = [
      { value: "Order Placed", label: "Order Placed" },
      { value: "Order Confirmed", label: "Order Confirmed" },
      { value: "Order Processing", label: "Order Processing" },
      { value: "Ready for Pickup", label: "Ready For Pickup" },
      { value: "Order Picked Up", label: "Order Picked Up" },
      { value: "Out for Delivery", label: "Out For Delivery" },
      { value: "Near Customer", label: "Near Customer" },
      { value: "Delivered", label: "Delivered" },
      { value: "Cancelled", label: "Cancelled" },
    ];

    const visibleOptions = isStore
      ? baseOptions.slice(0, baseOptions.findIndex((o) => o.value === "Ready for Pickup") + 1)
      : baseOptions;

    if (!isPharma) return visibleOptions;

    // Pharma-specific logic
    const pharmaOptions: OptionType[] = [];

    // Standard statuses that should hide pharma options
    const standardFlowStatuses = [
      "Order Placed",
      "Order Confirmed",
      "Order Processing",
      "Ready for Pickup",
      "Order Picked Up",
      "Out for Delivery",
      "Near Customer",
      "Delivered",
    ];

    if (!isPharma || standardFlowStatuses.includes(status)) return visibleOptions;

    if (isStore) return visibleOptions;

    // Filter pharmacy statuses based on current progress
    if (status === "Prescription Requested") {
      pharmaOptions.push(
        { value: "Prescription Requested", label: "Prescription Requested" },
        { value: "Prescription Accepted", label: "Prescription Accepted" },
        { value: "Prescription Rejected", label: "Prescription Rejected" },
      );
    } else if (
      status === "Prescription Accepted" ||
      status === "Creating Invoice" ||
      status === "Invoice Shared" ||
      status === "Invoice Accepted"
    ) {
      pharmaOptions.push({
        value: "Prescription Accepted",
        label: "Prescription Accepted",
      });
      if (status === "Prescription Accepted" || status === "Creating Invoice") {
        pharmaOptions.push({
          value: "Creating Invoice",
          label: "Creating Invoice",
        });
      }
      if (status === "Creating Invoice" || status === "Invoice Shared" || status === "Invoice Accepted") {
        pharmaOptions.push({
          value: "Invoice Shared",
          label: "Invoice Shared",
        });
      }
      if (status === "Invoice Accepted") {
        pharmaOptions.push({
          value: "Invoice Accepted",
          label: "Invoice Accepted",
        });
      }
    } else if (status === "Prescription Rejected" || status === "Invoice Rejected") {
      pharmaOptions.push({ value: status, label: status });
    }

    return [...pharmaOptions, ...baseOptions];
  }, [order, isStore]);

  const handleStatusChange = async (options: string[]) => {
    const newStatus = options[0];
    if (!newStatus || newStatus === selectedOrderOption) return;

    if (newStatus === "Accepted") {
      showModal(
        <AcceptOrderModal
          orderId={order.id}
          hideModal={hideModal}
          onSuccess={() => setSelectedOrderOption("Accepted")}
        />,
      );
      return;
    }

    if (newStatus === "Cancelled" || newStatus === "Rejected") {
      showModal(
        <RejectOrderModal
          orderId={order.id}
          hideModal={hideModal}
          status={newStatus}
          onSuccess={() => {
            if (onStatusUpdate) onStatusUpdate(newStatus);
            setSelectedOrderOption(newStatus);
          }}
        />,
      );
      return;
    }

    if (newStatus === "Order Confirmed") {
      try {
        await Order.updateStatus(order.id, newStatus);
        if (onStatusUpdate) {
          onStatusUpdate(newStatus);
        }
        setSelectedOrderOption(newStatus);
        toast.success(`Order status updated to ${newStatus}`);

        setTimeout(() => {
          showModal(
            <OrderProcessingModal
              orderId={order.id}
              hideModal={hideModal}
              onSuccess={async (status: string) => {
                if (onStatusUpdate) onStatusUpdate(status);
                setSelectedOrderOption(status);
              }}
            />,
          );
        }, 500);
      } catch (error) {
        console.error("Failed to update status", error);
        toast.error("Failed to update status to Order Confirmed");
      }
      return;
    }

    // Default update for other statuses
    try {
      await Order.updateStatus(order.id, newStatus);
      if (onStatusUpdate) {
        onStatusUpdate(newStatus);
      }
      setSelectedOrderOption(newStatus);
      toast.success(`Order status updated to ${newStatus}`);
    } catch (error: any) {
      console.error("Failed to update status", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update status";
      toast.error(errorMessage);
    }
  };

  return (
    <S.StyleShippingInfoContainer>
      <Typography size={20} weight={600}>
        Order Setup
      </Typography>
      <StyleDiv $marginTop={-22}>
        <S.SectionLabel>Change Order Status</S.SectionLabel>
        <MultiSelect
          options={orderStatusOptions}
          selected={[selectedOrderOption].filter(Boolean)}
          onChange={handleStatusChange}
          placeholder="Select order status"
          multiple={false}
        />
      </StyleDiv>

      <StyleDiv>
        {!isStore && (
          <Button
            title={
              order?.deliveryMan
                ? "Delivery Man Assigned"
                : order?.status !== "Order Processing"
                  ? "Process Order First"
                  : "Assign Delivery Man"
            }
            width={100}
            disabled={
              !!order?.deliveryMan || order?.status !== "Order Processing"
            }
            onClick={() => {
              // Only allow assignment if order is in "Order Processing" status and no delivery boy assigned
              if (!order?.deliveryMan && order?.status === "Order Processing") {
                showModal(
                  <AssignDeliveryManModal
                    orderId={order?.id}
                    currentId={order?.deliveryMan?.id}
                    hideModal={hideModal}
                    onSuccess={(newStatus: string) => {
                      if (onStatusUpdate) onStatusUpdate(newStatus);
                      setSelectedOrderOption(newStatus);
                    }}
                  />,
                  { width: 1200 },
                );
              } else if (order?.status !== "Order Processing") {
                toast.warning(
                  "Order must be in 'Order Processing' status before assigning delivery personnel",
                );
              }
            }}
          />
        )}
        <br />
        {order?.deliveryMan && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "8px",
            }}
          >
            {/* Avatar with initials fallback */}
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "#10B981", // green color
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                flexShrink: 0,
              }}
            >
              {order.deliveryMan.avatar ? (
                <img
                  src={order.deliveryMan.avatar}
                  alt={order.deliveryMan.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                order.deliveryMan.name
                  ?.split(" ")
                  .map((word) => word[0])
                  .join("")
                  .toUpperCase()
                  .substring(0, 2)
              )}
            </div>

            {/* Delivery man info */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Typography styleText="font-size:12px; color:#6B7280; margin-bottom:2px;">
                Assigned to
              </Typography>
              <Typography styleText="font-size:14px; font-weight:500; color:#111827;">
                {order.deliveryMan.fullName || order.deliveryMan.name}
              </Typography>
            </div>
          </div>
        )}
      </StyleDiv>
      <PrescriptionCountdown order={order} />
      <TrackId order={order} />
    </S.StyleShippingInfoContainer>
  );
};

export default OrderShippingCard;

const PrescriptionCountdown = ({ order }: { order: any }) => {
  const [timeLeft, setTimeLeft] = useState<number | null>(order?.remainingSeconds ?? null);
  const [timerType, setTimerType] = useState<"acceptance" | "invoice" | null>(null);

  useEffect(() => {
    setTimeLeft(order?.remainingSeconds ?? null);
    
    if (order.status === "Prescription Requested" || order.status === "Viewing Order") {
      setTimerType("acceptance");
    } else if (order.status === "Prescription Accepted" || order.status === "Creating Invoice") {
      setTimerType("invoice");
    } else {
      setTimerType(null);
    }
  }, [order?.remainingSeconds, order?.status]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft]);

  if (!timerType || timeLeft === null) return null;

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  
  const isDanger = timerType === "invoice" ? minutes < 5 : minutes < 1;
  const isExpired = timeLeft === 0;

  let title = timerType === "acceptance" ? "Time to Accept:" : "Time to Create Invoice:";
  if (isExpired) {
    title = timerType === "acceptance" ? "Order Rerouted" : "Invoice Timeout Rerouting";
  }

  return (
    <div
      style={{
        padding: "12px",
        borderRadius: "8px",
        backgroundColor: isExpired ? "#fee2e2" : isDanger ? "#ffedd5" : "#f0fdf4",
        border: `1px solid ${isExpired ? "#ef4444" : isDanger ? "#f97316" : "#22c55e"}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "16px",
      }}
    >
      <Typography styleText={`font-size:14px; font-weight:600; color:${isExpired ? "#991b1b" : isDanger ? "#9a3412" : "#166534"}`}>
        {title}
      </Typography>
      {!isExpired && (
        <Typography styleText={`font-size:18px; font-weight:700; color:${isDanger ? "#dc2626" : "#16a34a"}`}>
          {minutes.toString().padStart(2, "0")}:{seconds.toString().padStart(2, "0")}
        </Typography>
      )}
    </div>
  );
};

const TrackId = ({ order }: any) => {
  const navigate = useNavigate();
  return (
    <S.TrackContainer>
      <Truck />
      <S.TrackInfo>
        <S.StatusText>{order?.status || "Pending"}</S.StatusText>
        <S.TrackIdText>Order ID: {order?.id}</S.TrackIdText>
        {order?.trackingId && (
          <S.TrackIdText>Track ID: {order.trackingId}</S.TrackIdText>
        )}
      </S.TrackInfo>
    </S.TrackContainer>
  );
};
